<template>
  <SfModalPortal
    modal-id="reports/save-forecast-modal"
    size="xs"
    @opened="openModal"
    @close="closeModal"
  >
    <template #title>{{ modalTitle }}</template>

    <template #body>
      <div class="tw-flex tw-flex-col tw-gap-3">
        <!-- New budget option -->
        <label
          class="
            tw-cursor-pointer
            tw-rounded-lg
            tw-border-2
            tw-border-solid
            tw-p-4
            tw-flex
            tw-items-start
            tw-gap-3
          "
          :class="
            selectedOption === 'new'
              ? 'tw-border-sf-primary tw-bg-cyan-50/50'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white'
          "
          data-testid="new-budget-option"
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="save-forecast-option"
            value="new"
            class="tw-mt-0.5"
          />
          <div class="tw-flex-1">
            <div class="tw-text-base tw-font-medium">
              {{ copy.newBudget.title }}
            </div>
            <div
              class="tw-text-sm"
              :class="
                selectedOption === 'new'
                  ? 'tw-text-sf-primary-dark'
                  : 'tw-text-sf-neutral-light-2'
              "
            >
              {{ copy.newBudget.subtitle }}
            </div>

            <div v-if="selectedOption === 'new'" class="tw-mt-3">
              <SfInput
                v-model="newBudgetName"
                focused
                :label="copy.newBudget.nameLabel"
                :placeholder="namePlaceholder"
                data-testid="new-budget-name-input"
              />
            </div>
          </div>
        </label>

        <!-- Existing budget option -->
        <label
          class="
            tw-cursor-pointer
            tw-rounded-lg
            tw-border-2
            tw-border-solid
            tw-p-4
            tw-flex
            tw-items-start
            tw-gap-3
          "
          :class="
            selectedOption === 'existing'
              ? 'tw-border-sf-primary tw-bg-cyan-50/50'
              : 'tw-border-sf-neutral-lighter-2 tw-bg-white'
          "
          data-testid="existing-budget-option"
        >
          <input
            v-model="selectedOption"
            type="radio"
            name="save-forecast-option"
            value="existing"
            class="tw-mt-0.5"
          />
          <div class="tw-flex-1">
            <div class="tw-text-base tw-font-medium">
              {{ copy.existingBudget.title }}
            </div>
            <div
              class="tw-text-sm"
              :class="
                selectedOption === 'existing'
                  ? 'tw-text-sf-primary-dark'
                  : 'tw-text-sf-neutral-light-2'
              "
            >
              {{ copy.existingBudget.subtitle }}
            </div>

            <div v-if="selectedOption === 'existing'" class="tw-mt-3">
              <label class="tw-mb-1 tw-block tw-text-sm tw-font-normal">
                {{ copy.existingBudget.dropdownLabel }}
              </label>
              <select
                v-model="selectedBudgetId"
                class="tw-w-full"
                :disabled="!budgets.length"
                data-testid="save-budget-dropdown"
              >
                <option v-if="!budgets.length" :value="null">
                  {{ copy.noBudgets }}
                </option>
                <option
                  v-for="budget in budgets"
                  :key="budget.id"
                  :value="budget.id"
                >
                  {{ budget.name }}
                </option>
              </select>

              <SfBannerInline
                v-if="showExistingDataWarning"
                variant="warning"
                :title="copy.existingBudget.existingDataWarning"
                class="tw-mt-3"
                data-testid="existing-data-warning"
              />
            </div>
          </div>
        </label>
      </div>
    </template>

    <template #footer>
      <SfButtonDs variant="secondary" @click="closeModal">
        {{ copy.cancelButton }}
      </SfButtonDs>
      <SfButtonDs
        variant="primary"
        :disabled="saveDisabled"
        data-testid="save-forecast-submit"
        @click="handleSave"
      >
        {{ copy.saveButton }}
      </SfButtonDs>
    </template>
  </SfModalPortal>
</template>

<script setup lang="ts">
import type { Payload } from '../types/save-forecast-modal'
import type * as SaveModalDataResponse from '../types/api/save-modal-data-response'

import { ref, computed } from 'vue'

import useI18n from '@app/shared/vue/composables/use-I18n'
import SFetch from '@app/utils/sfetch'

import { SfModalPortal } from '@app/shared/vue/components/sf-modal'
import { SfButtonDs } from '@silverfin/ui-components'
import { SfInput } from '@app/shared/vue/components/sf-input'
import SfBannerInline from '@app/shared/vue/components/sf-banner/sf-banner-inline.vue'

const I18n = useI18n()

const copy = {
  newBudget: {
    title: I18n.t('reports.forecast.save_forecast_modal.new_budget.title'),
    subtitle: I18n.t(
      'reports.forecast.save_forecast_modal.new_budget.subtitle'
    ),
    nameLabel: I18n.t(
      'reports.forecast.save_forecast_modal.new_budget.name_label'
    ),
  },
  existingBudget: {
    title: I18n.t('reports.forecast.save_forecast_modal.existing_budget.title'),
    subtitle: I18n.t(
      'reports.forecast.save_forecast_modal.existing_budget.subtitle'
    ),
    dropdownLabel: I18n.t(
      'reports.forecast.save_forecast_modal.existing_budget.dropdown_label'
    ),
    existingDataWarning: I18n.t(
      'reports.forecast.save_forecast_modal.existing_budget.existing_data_warning'
    ),
  },
  noBudgets: I18n.t('reports.forecast.save_forecast_modal.no_budgets'),
  saveButton: I18n.t('reports.forecast.save_forecast_modal.save_button'),
  cancelButton: I18n.t('common.cancel'),
}

const targetYear = ref(0)

const modalTitle = computed(() =>
  I18n.t('reports.forecast.save_forecast_modal.title', {
    year: targetYear.value,
  })
)

const namePlaceholder = computed(() =>
  I18n.t('reports.forecast.save_forecast_modal.new_budget.name_placeholder', {
    year: targetYear.value,
  })
)

const selectedOption = ref<'new' | 'existing'>('new')
const newBudgetName = ref('')
const budgets = ref<SaveModalDataResponse.Budget[]>([])
const selectedBudgetId = ref<number | null>(null)

const selectedBudget = computed(
  () => budgets.value.find((b) => b.id === selectedBudgetId.value) ?? null
)

const showExistingDataWarning = computed(
  () => selectedBudget.value?.hasDataForTargetYear === true
)

const saveDisabled = computed(() => {
  if (selectedOption.value === 'new') return !newBudgetName.value.trim()
  return !budgets.value.length
})

let modalPayload: Payload

async function openModal(localModalPayload: Payload) {
  modalPayload = localModalPayload
  targetYear.value = modalPayload.targetYear
  selectedOption.value = modalPayload.presetBudgetId ? 'existing' : 'new'
  newBudgetName.value = ''
  budgets.value = []
  selectedBudgetId.value = null

  await fetchBudgets()
}

async function fetchBudgets() {
  const url = window.Silverfin.Routes.report_forecast_save_modal_data_path(
    window.Silverfin.currentFirmId,
    window.Silverfin.currentCompanyId,
    modalPayload.reportId,
    { target_year: modalPayload.targetYear }
  )

  try {
    const data = (await SFetch.json(url)) as SaveModalDataResponse.Data
    budgets.value = data.budgets
    if (budgets.value.length) {
      const presetBudget = budgets.value.find(
        (b) => b.id === modalPayload.presetBudgetId
      )
      selectedBudgetId.value = presetBudget?.id ?? budgets.value[0].id
    }
  } catch {
    alert(I18n.t('common.error.try_again'))
  }
}

function handleSave() {
  closeModal()

  modalPayload.onSave({
    budgetOption: selectedOption.value,
    budgetName: newBudgetName.value,
    budgetId: selectedBudgetId.value,
  })
}

function closeModal() {
  window.modalCitizen.closeModalPortal()
}
</script>
